import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Button,
  Box,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  getTableConfig,
  STATUS_COLOR_MAP,
  ONBOARDING_STATUS_COLOR_MAP,
  FIELD_EXTRACTORS,
  TABLE_TYPES,
} from "../config/tableConfig";
import { Add } from "@mui/icons-material";

const BADGE_TINTS = {
  success: { bg: "#EAFBF2", color: "#0C7A45" },
  error: { bg: "#FDECEC", color: "#B3261E" },
  warning: { bg: "#FEF6E7", color: "#95660A" },
  teal: { bg: "#E6F5F2", color: "#0B5F56" },
  urgent: { bg: "#FEF0E1", color: "#B45309" },
  blue: { bg: "#EAF2FE", color: "#1D4ED8" },
  purple: { bg: "#F3EEFE", color: "#6D28D9" },
  pink: { bg: "#FDEEF6", color: "#BE185D" },
  default: { bg: "#F1F3F2", color: "#5C6864" },
};

const FINANCIER_PALETTE = [
  "teal",
  "blue",
  "purple",
  "warning",
  "pink",
  "success",
];

const getBadgeTint = (colorKey) => BADGE_TINTS[colorKey] || BADGE_TINTS.default;

const STATUS_CHIP_COLUMNS = {
  status: STATUS_COLOR_MAP,
  onboardingStatus: ONBOARDING_STATUS_COLOR_MAP,
};

const formatChipLabel = (value) => {
  if (!value || value === "--") return "--";
  return String(value)
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getFinancierColorKey = (value) => {
  if (!value || value === "--") return "default";
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % FINANCIER_PALETTE.length;
  return FINANCIER_PALETTE[index];
};

const TableView = ({ tableType, data, dialogs, isFetching, onMenuOpen }) => {
  const config = getTableConfig(tableType);

  const getCellValue = (item, columnId) => {
    if (FIELD_EXTRACTORS[columnId]) {
      return FIELD_EXTRACTORS[columnId](item);
    }
    const value = item[columnId];
    return value || "--";
  };

  const getStatusColorKey = (status, map) => map[status] || "default";

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 3,
        border: "1px solid #E6ECEA",
        boxShadow: "0 4px 16px rgba(16,24,22,0.06)",
        overflowX: "auto",
        opacity: isFetching ? 0.6 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <Table stickyHeader sx={{ minWidth: 750 }}>
        <TableHead>
          <TableRow>
            {config.columns.map((column) =>
              column.id === "action" ? (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  sx={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#FAFBFA",
                    zIndex: 10,
                    minWidth: column.width,
                    fontWeight: 700,
                    fontSize: 11.5,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#66766F",
                    borderLeft: "1px dashed #E6ECEA",
                    borderBottom: "1px solid #E6ECEA",
                  }}
                >
                  {column.label}
                </TableCell>
              ) : (
                <TableCell
                  key={column.id}
                  sx={{
                    fontWeight: 700,
                    fontSize: 11.5,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#66766F",
                    backgroundColor: "#FAFBFA",
                    borderBottom: "1px solid #E6ECEA",
                    minWidth: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow
              key={item.id}
              hover
              sx={{
                "&:hover": { backgroundColor: "#FAFCFB" },
                "& td": { borderBottom: "1px solid #E6ECEA" },
                "&:last-child td": { borderBottom: "none" },
              }}
            >
              {config.columns.map((column) => {
                if (column.id === "action") {
                  return tableType === TABLE_TYPES.PRE_ONBOARDING ||
                    tableType === TABLE_TYPES.LEADS ? (
                    <TableCell
                      key={column.id}
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        borderLeft: "1px dashed #E6ECEA",
                        zIndex: 10,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => onMenuOpen(e, item)}
                        sx={{
                          color: "#66766F",
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: "#E6F5F2",
                            color: "#0B5F56",
                          },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  ) : (
                    <TableCell
                      key={column.id}
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        borderLeft: "1px dashed #E6ECEA",
                        zIndex: 10,
                      }}
                    >
                      <Button
                        size="small"
                        disabled={
                          item.status === "Completed" ||
                          item.status === "completed"
                        }
                        onClick={() => {
                          dialogs.rowActions.handleCardAddition(item);
                        }}
                        sx={{
                          padding: 0.5,
                          px: 1.5,
                          minHeight: 25,
                          borderRadius: 5,
                          backgroundColor: "#E6F5F2",
                          color: "#0B5F56",
                          boxShadow: "none",
                          fontWeight: 650,
                          "&:hover": {
                            backgroundColor: "#0E7A6E",
                            color: "#fff",
                            boxShadow: "none",
                          },
                        }}
                        startIcon={<Add />}
                      >
                        Card
                      </Button>
                    </TableCell>
                  );
                }

                if (
                  STATUS_CHIP_COLUMNS[column.id] ||
                  column.id === "financierType"
                ) {
                  const rawValue = getCellValue(item, column.id);
                  const label = formatChipLabel(rawValue);

                  const colorKey =
                    column.id === "financierType"
                      ? getFinancierColorKey(rawValue)
                      : getStatusColorKey(
                          rawValue,
                          STATUS_CHIP_COLUMNS[column.id],
                        );

                  const tint = getBadgeTint(colorKey);
                  const isUrgent = colorKey === "urgent";

                  return (
                    <TableCell key={column.id}>
                      <Chip
                        label={label}
                        size="small"
                        icon={
                          isUrgent ? (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: tint.color,
                                ml: "8px !important",
                              }}
                            />
                          ) : undefined
                        }
                        sx={{
                          backgroundColor: tint.bg,
                          color: tint.color,
                          fontWeight: 650,
                          fontSize: 12,
                          border: "none",
                          maxWidth:
                            column.id === "onboardingStatus" ? 160 : undefined,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                          "& .MuiChip-icon": { color: "inherit" },
                        }}
                      />
                    </TableCell>
                  );
                }

                return (
                  <TableCell
                    key={column.id}
                    sx={{
                      whiteSpace: column?.whiteSpace || "wrap",
                      fontWeight: column.id === "prn" ? 650 : 400,
                      color: column.id === "prn" ? "#12181A" : "#3A4441",
                    }}
                  >
                    {getCellValue(item, column.id)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableView;
